import { useState, useRef, useEffect, useCallback } from "react";
import { Check, Pencil, Zap, Flag, Undo2 } from "lucide-react";
import type { VerifiableField, RuleSuggestion } from "../../api/client";
import { prettyFieldLabel, prettyValue } from "../../utils/labels";

interface FieldCardProps {
  field: VerifiableField;
  isFocused: boolean;
  onVerify: (fieldPath: string) => void;
  onCorrect: (fieldPath: string, newValue: unknown) => void;
  onFocus: (fieldPath: string) => void;
  onFlag?: (fieldPath: string, notes?: string) => void;
  onRevert?: (fieldPath: string) => void;
  ruleApplied?: string | null;
  suggestion?: RuleSuggestion | null;
  onApproveSuggestion?: (suggestion: RuleSuggestion) => void;
}

function formatValue(val: unknown): string {
  if (val === null || val === undefined) return "--";
  if (typeof val === "number") {
    if (Number.isInteger(val) && Math.abs(val) >= 1000) {
      return val.toLocaleString("en-US");
    }
    return val.toLocaleString("en-US", { maximumFractionDigits: 2 });
  }
  return String(val);
}

// Use canonical label registry
const fieldLabel = prettyFieldLabel;

export default function FieldCard({
  field,
  isFocused,
  onVerify,
  onCorrect,
  onFocus,
  onFlag,
  onRevert,
  ruleApplied,
  suggestion,
  onApproveSuggestion,
}: FieldCardProps) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  useEffect(() => {
    if (isFocused && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [isFocused]);

  const startEdit = useCallback(() => {
    setEditValue(formatValue(field.value));
    setEditing(true);
  }, [field.value]);

  const commitEdit = useCallback(() => {
    if (!editing) return;
    setEditing(false);
    const trimmed = editValue.trim();
    const currentStr = formatValue(field.value);
    if (trimmed === currentStr || trimmed === "") return;

    // Try to parse as number
    const cleaned = trimmed.replace(/,/g, "");
    const asNum = Number(cleaned);
    const newVal = !isNaN(asNum) && cleaned !== "" ? asNum : trimmed;
    onCorrect(field.field_path, newVal);
  }, [editing, editValue, field.value, field.field_path, onCorrect]);

  const cancelEdit = useCallback(() => {
    setEditing(false);
  }, []);

  // Single keyboard handler — Shift+Enter MUST be checked before Enter
  // to prevent verify from firing when the user intends to edit.
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (editing) {
        if (e.key === "Enter") { e.preventDefault(); commitEdit(); }
        else if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
        return;
      }
      if (e.shiftKey && e.key === "Enter") {
        e.preventDefault();
        startEdit();
        return;
      }
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onVerify(field.field_path);
      }
    },
    [editing, commitEdit, cancelEdit, startEdit, onVerify, field.field_path],
  );

  const borderClass =
    field.state === "verified"
      ? "border-l-4 border-l-sage-400"
      : field.state === "corrected"
      ? "border-l-4 border-l-honey-500"
      : field.state === "flag"
      ? "border-l-4 border-l-amber-400"
      : "border-l-4 border-l-transparent";

  const focusRing = isFocused ? "ring-2 ring-forest-400 ring-offset-1" : "";

  return (
    <div
      ref={cardRef}
      tabIndex={0}
      role="button"
      className={`group relative bg-white rounded-lg border border-grove-300 px-4 py-3 transition-all duration-200 cursor-pointer hover:shadow-sm ${borderClass} ${focusRing}`}
      onClick={() => onFocus(field.field_path)}
      onKeyDown={handleKeyDown}
      onFocus={() => onFocus(field.field_path)}
    >
      {/* Saving indicator */}
      {field.state === "verified" && (
        <span className="absolute top-2 right-2 text-sage-500">
          <Check className="w-4 h-4" />
        </span>
      )}

      {/* Label */}
      <div
        className={`text-xs font-medium mb-1 ${
          field.state === "unverified" ? "text-bark-400" : "text-bark-600"
        }`}
      >
        {fieldLabel(field.field_path)}
        {field.state === "unverified" && field.confidence != null && (
          <span
            className={`ml-1 inline-block w-2 h-2 rounded-full ${
              field.confidence >= 85 ? "bg-sage-400" :
              field.confidence >= 60 ? "bg-honey-500" : "bg-red-400"
            }`}
            title={`Confidence: ${field.confidence}%`}
          />
        )}
        {field.is_critical && (
          <span className="ml-1.5 text-[10px] font-semibold text-honey-600 uppercase tracking-wider">
            required
          </span>
        )}
      </div>

      {/* Value */}
      {editing ? (
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={handleKeyDown}
          className="w-full px-2 py-1 text-sm font-mono tabular-nums border border-forest-300 rounded focus:outline-none focus:ring-1 focus:ring-forest-500 bg-grove-50"
        />
      ) : (
        <div className="flex items-baseline gap-2">
          {field.state === "corrected" && field.original_value !== null && (
            <span className="text-xs text-bark-400 line-through tabular-nums">
              {prettyValue(field.original_value)}
            </span>
          )}
          <span
            className={`text-sm font-medium tabular-nums whitespace-nowrap ${
              field.state === "corrected"
                ? "text-forest-700 underline decoration-honey-500 decoration-2 underline-offset-2"
                : "text-forest-700"
            }`}
          >
            {prettyValue(field.value)}
          </span>
        </div>
      )}

      {/* Rule applied tag */}
      {ruleApplied && (
        <div className="mt-1.5 group/rule relative inline-block">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-sage-100 text-sage-600 border border-sage-200">
            <Zap className="w-3 h-3" />
            Auto-corrected by rule
          </span>
          <div className="absolute left-0 bottom-full mb-1 w-56 p-2 bg-bark-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover/rule:opacity-100 transition-opacity pointer-events-none z-10">
            {ruleApplied}
          </div>
        </div>
      )}

      {/* Rule suggestion callout */}
      {suggestion && onApproveSuggestion && (
        <div className="mt-2 px-3 py-2 bg-honey-50 border border-honey-300 rounded-lg">
          <p className="text-xs text-bark-600 mb-1.5">
            {suggestion.description}
          </p>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onApproveSuggestion(suggestion);
            }}
            className="px-2.5 py-1 text-[11px] font-medium rounded-md bg-honey-600 text-white hover:bg-honey-700 transition-colors"
          >
            Make it a rule
          </button>
        </div>
      )}

      {/* Flagged badge */}
      {field.state === "flag" && (
        <span className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-amber-100 text-amber-700 border border-amber-200">
          <Flag className="w-3 h-3" />
          Flagged for review
        </span>
      )}

      {/* Revert link for corrected fields */}
      {field.state === "corrected" && onRevert && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRevert(field.field_path);
          }}
          className="mt-1 inline-flex items-center gap-1 text-[10px] text-bark-400 hover:text-forest-600 transition-colors opacity-0 group-hover:opacity-100"
        >
          <Undo2 className="w-3 h-3" />
          Revert to parser value
        </button>
      )}

      {/* Action buttons (visible on hover or focus) */}
      {!editing && (field.state === "unverified" || field.state === "flag") && (
        <div className="absolute right-2 bottom-2 flex gap-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onVerify(field.field_path);
            }}
            className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-forest-600 text-white hover:bg-forest-700 transition-colors"
          >
            Verify
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              startEdit();
            }}
            className="p-1 rounded-full text-bark-400 hover:text-forest-700 hover:bg-grove-100 transition-colors"
          >
            <Pencil className="w-3 h-3" />
          </button>
          {onFlag && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onFlag(field.field_path);
              }}
              className="p-1 rounded-full text-bark-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
              title="Flag for review"
            >
              <Flag className="w-3 h-3" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
